import { useState, useEffect } from "react";
import api, { fmtMoney, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, AlertCircle, FileText, FileX2, FileWarning, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TIPO_REL_LABEL = {
  "01": "Nota de Crédito",
  "02": "Devolución",
  "03": "Devolución sobre facturas anteriores",
  "04": "Sustitución",
  "05": "Traslado",
  "06": "Trasladado a",
  "07": "CFDI por anticipos",
};

const MES_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                   "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const ymToLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return `${MES_NAMES[parseInt(m,10)-1]} ${y}`;
};

/**
 * Diálogo de diagnóstico de un UUID. Muestra TODA la trazabilidad SAT:
 * - Datos del CFDI (tipo, fecha, totales, estado)
 * - Cobros recibidos vía Complemento de Pago
 * - Notas de Crédito que lo afectan
 * - Filas de Excel del contador
 * - En qué meses se contabiliza para Confronta/RESICO
 *
 * Props:
 *   open, onClose, clientId, initialUuid (opcional)
 */
export default function DiagnoseUuidDialog({ open, onClose, clientId, initialUuid = "" }) {
  const [uuid, setUuid] = useState(initialUuid);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (u) => {
    const v = (u || uuid || "").trim().toUpperCase();
    if (!v) { toast.warning("Pega un UUID"); return; }
    if (!clientId) { toast.error("Selecciona un cliente primero"); return; }
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.get("/diagnose/uuid", { params: { client_id: clientId, uuid: v } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "UUID no encontrado");
    } finally {
      setLoading(false);
    }
  };

  // Si initialUuid cambia (al abrir desde una tabla), dispara lookup
  useEffect(() => {
    if (open && initialUuid && initialUuid !== uuid) {
      setUuid(initialUuid);
      lookup(initialUuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialUuid]);

  const handleClose = () => {
    setUuid("");
    setData(null);
    onClose();
  };

  const cfdi = data?.cfdi;
  const isNC = cfdi?.tipo_comprobante === "E";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 flex flex-row items-center justify-between gap-4">
          <DialogTitle className="text-[#0F2D5C] flex items-center gap-2">
            <Search className="w-5 h-5" /> Diagnóstico de UUID
          </DialogTitle>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-sm hover:bg-slate-100 flex items-center justify-center text-slate-500"
            data-testid="diagnose-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </DialogHeader>

        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex gap-2">
            <Input
              data-testid="diagnose-uuid-input"
              placeholder="Pega un UUID (folio fiscal) — ej: 01ABFA59-FFC3-4D59-ABE2-3877132DF7AC"
              value={uuid}
              onChange={(e) => setUuid(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              className="font-mono text-xs uppercase"
            />
            <Button
              onClick={() => lookup()}
              disabled={loading}
              data-testid="diagnose-search-btn"
              className="bg-[#0F2D5C] hover:bg-[#0b2249] text-white"
            >
              <Search className="w-4 h-4 mr-1.5" /> Buscar
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && (
            <div className="text-center py-12 text-sm text-slate-500">Cargando...</div>
          )}

          {!loading && !data && (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" strokeWidth={1.2} />
              <p className="text-sm">Pega un UUID arriba y verás dónde se está contabilizando.</p>
              <p className="text-xs text-slate-400 mt-1">
                Útil para verificar PUE vs PPD, parcialidades, Notas de Crédito y filas del Excel del contador.
              </p>
            </div>
          )}

          {!loading && data && !cfdi && data.excel_rows?.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex gap-3" data-testid="diagnose-not-found">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <div className="font-semibold text-amber-900">UUID no encontrado</div>
                <div className="text-xs text-amber-800 mt-1">
                  El UUID <span className="font-mono">{data.uuid}</span> no existe ni en CFDIs cargados ni en filas del Excel para este cliente.
                </div>
              </div>
            </div>
          )}

          {!loading && data && cfdi && <CfdiCard cfdi={cfdi} />}

          {!loading && data && data.pagos_que_lo_cobran?.length > 0 && (
            <PagosCard pagos={data.pagos_que_lo_cobran} />
          )}

          {!loading && data && data.ncs_aplicadas?.length > 0 && (
            <NCsAplicadasCard ncs={data.ncs_aplicadas} />
          )}

          {!loading && data && isNC && data.afecta_a?.length > 0 && (
            <AfectaACard afecta={data.afecta_a} />
          )}

          {!loading && data && data.excel_rows?.length > 0 && (
            <ExcelRowsCard rows={data.excel_rows} />
          )}

          {!loading && data && data.contabilizado_en_meses?.length > 0 && (
            <ContabilizadoCard meses={data.contabilizado_en_meses} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CfdiCard({ cfdi }) {
  const isNC = cfdi.tipo_comprobante === "E";
  const isPUE = cfdi.metodo_pago === "PUE";
  const isCancel = cfdi.estado === "Cancelado";
  const tipoMap = { I: "Ingreso", E: "Nota de Crédito (E)", P: "Complemento de Pago", N: "Nómina", T: "Traslado" };
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
      <div className="px-4 py-2 bg-[#0F2D5C] text-white text-xs font-semibold flex items-center justify-between">
        <span className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" />
          CFDI {tipoMap[cfdi.tipo_comprobante] || cfdi.tipo_comprobante}
        </span>
        <span className="flex items-center gap-2">
          {isCancel && (
            <span className="px-2 py-0.5 bg-red-500 text-white rounded-sm text-[10px] uppercase">Cancelado</span>
          )}
          {!isCancel && isPUE && (
            <span className="px-2 py-0.5 bg-emerald-500 text-white rounded-sm text-[10px] uppercase">PUE</span>
          )}
          {!isCancel && cfdi.metodo_pago === "PPD" && (
            <span className="px-2 py-0.5 bg-amber-500 text-white rounded-sm text-[10px] uppercase">PPD</span>
          )}
          {isNC && (
            <span className="px-2 py-0.5 bg-violet-500 text-white rounded-sm text-[10px] uppercase">NC</span>
          )}
        </span>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Field label="UUID" value={<span className="font-mono text-[10px] break-all">{cfdi.uuid}</span>} />
        <Field label="Folio" value={`${cfdi.serie || ""}${cfdi.folio || "—"}`} />
        <Field label="Fecha emisión" value={(cfdi.fecha || "").slice(0, 10)} />
        <Field label="Estado" value={cfdi.estado} />
        <Field label="RFC contraparte" value={<span className="font-mono">{cfdi.rfc_receptor || cfdi.rfc_emisor}</span>} />
        <Field label="Razón social" value={cfdi.nombre_receptor || cfdi.nombre_emisor} className="col-span-3" />
        <Field label="Subtotal" value={fmtMoney(cfdi.subtotal || 0)} mono />
        <Field label="IVA" value={fmtMoney(cfdi.iva_trasladado || 0)} mono />
        <Field label="ISR Ret." value={fmtMoney(cfdi.isr_retenido || 0)} mono />
        <Field label="Total" value={fmtMoney(cfdi.total || 0)} mono bold />
      </div>
    </div>
  );
}

function PagosCard({ pagos }) {
  const tot = pagos.reduce((s, p) => s + (p.imp_pagado || 0), 0);
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
      <div className="px-4 py-2 bg-amber-100 text-amber-900 text-xs font-semibold flex justify-between">
        <span>Cobros recibidos (Complementos de Pago)</span>
        <span className="tabular-nums">{pagos.length} parcialidades · Total {fmtMoney(tot)}</span>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-slate-500">
            <th className="px-3 py-2 text-left">UUID Complemento</th>
            <th className="px-3 py-2 text-left">Fecha pago</th>
            <th className="px-3 py-2 text-center">#</th>
            <th className="px-3 py-2 text-right">Saldo anterior</th>
            <th className="px-3 py-2 text-right">Pagado</th>
            <th className="px-3 py-2 text-right">Saldo insoluto</th>
          </tr>
        </thead>
        <tbody>
          {pagos.map((p, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="px-3 py-1.5 font-mono text-[10px]">{p.uuid_complemento.slice(0, 8)}...{p.uuid_complemento.slice(-4)}</td>
              <td className="px-3 py-1.5">{p.fecha_pago}</td>
              <td className="px-3 py-1.5 text-center">{p.num_parcialidad || "—"}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{fmtMoney(p.imp_saldo_anterior)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-emerald-700">{fmtMoney(p.imp_pagado)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{fmtMoney(p.imp_saldo_insoluto)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NCsAplicadasCard({ ncs }) {
  return (
    <div className="bg-white rounded-md border border-violet-200 overflow-hidden">
      <div className="px-4 py-2 bg-violet-100 text-violet-900 text-xs font-semibold flex items-center gap-2">
        <FileX2 className="w-4 h-4" />
        Notas de Crédito que afectan este UUID
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-slate-500">
            <th className="px-3 py-2 text-left">UUID NC</th>
            <th className="px-3 py-2 text-left">Folio</th>
            <th className="px-3 py-2 text-left">Fecha</th>
            <th className="px-3 py-2 text-left">Tipo relación</th>
            <th className="px-3 py-2 text-right">Subtotal</th>
            <th className="px-3 py-2 text-right">IVA</th>
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {ncs.map((n, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="px-3 py-1.5 font-mono text-[10px]">{n.uuid.slice(0, 8)}...{n.uuid.slice(-4)}</td>
              <td className="px-3 py-1.5">{n.folio || "—"}</td>
              <td className="px-3 py-1.5">{n.fecha}</td>
              <td className="px-3 py-1.5 text-violet-700">
                {n.tipo_relacion} · {TIPO_REL_LABEL[n.tipo_relacion] || ""}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-red-600">−{fmtMoney(n.subtotal)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-red-600">−{fmtMoney(n.iva)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-red-700">−{fmtMoney(n.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AfectaACard({ afecta }) {
  return (
    <div className="bg-white rounded-md border border-violet-200 overflow-hidden">
      <div className="px-4 py-2 bg-violet-100 text-violet-900 text-xs font-semibold flex items-center gap-2">
        <ArrowRight className="w-4 h-4" />
        Esta NC afecta a las siguientes facturas
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-slate-500">
            <th className="px-3 py-2 text-left">UUID afectado</th>
            <th className="px-3 py-2 text-left">Tipo relación</th>
            <th className="px-3 py-2 text-left">Fecha</th>
            <th className="px-3 py-2 text-right">Total original</th>
            <th className="px-3 py-2 text-center">Estado</th>
          </tr>
        </thead>
        <tbody>
          {afecta.map((a, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="px-3 py-1.5 font-mono text-[10px]">{a.uuid}</td>
              <td className="px-3 py-1.5">{a.tipo_relacion} · {TIPO_REL_LABEL[a.tipo_relacion] || ""}</td>
              <td className="px-3 py-1.5">{a.fecha || "—"}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{a.total != null ? fmtMoney(a.total) : "—"}</td>
              <td className="px-3 py-1.5 text-center">
                {a.encontrado ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{a.estado || "Correcto"}</span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">No cargado</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExcelRowsCard({ rows }) {
  return (
    <div className="bg-white rounded-md border border-blue-200 overflow-hidden">
      <div className="px-4 py-2 bg-blue-100 text-blue-900 text-xs font-semibold flex items-center gap-2">
        <FileWarning className="w-4 h-4" />
        Filas en el Excel del contador con este UUID ({rows.length})
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-slate-500">
            <th className="px-3 py-2 text-left">Fecha</th>
            <th className="px-3 py-2 text-left">Folio</th>
            <th className="px-3 py-2 text-center">Sección</th>
            <th className="px-3 py-2 text-right">Subtotal</th>
            <th className="px-3 py-2 text-right">IVA</th>
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const seccion = r.es_venta ? "Ingreso" : r.es_gasto ? "Gasto" : r.es_nomina ? "Nómina" : r.direccion;
            return (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-3 py-1.5">{(r.fecha_ingreso || "").slice(0, 10)}</td>
                <td className="px-3 py-1.5">{r.folio || "—"}</td>
                <td className="px-3 py-1.5 text-center text-[10px]">{seccion}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtMoney(r.subtotal_cobrado || 0)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmtMoney(r.iva_cobrado || 0)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtMoney(r.total_cobrado)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ContabilizadoCard({ meses }) {
  // Agrupa por mes
  const byMes = {};
  meses.forEach(m => {
    if (!m.mes) return;
    byMes[m.mes] = byMes[m.mes] || { mes: m.mes, items: [], total: 0 };
    byMes[m.mes].items.push(m);
    byMes[m.mes].total += m.monto || 0;
  });
  const sorted = Object.values(byMes).sort((a, b) => a.mes.localeCompare(b.mes));
  if (sorted.length === 0) return null;

  return (
    <div className="bg-white rounded-md border border-emerald-200 overflow-hidden">
      <div className="px-4 py-2 bg-emerald-100 text-emerald-900 text-xs font-semibold flex items-center gap-2">
        <Calendar className="w-4 h-4" />
        Contabilización en Confronta / RESICO (Sistema CFDI)
      </div>
      <div className="p-3 space-y-2">
        {sorted.map(m => (
          <div key={m.mes} className="flex items-start gap-3 text-xs border-b border-slate-100 pb-2 last:border-0">
            <div className="w-32 font-semibold text-[#0F2D5C]">{ymToLabel(m.mes)}</div>
            <div className="flex-1 space-y-1">
              {m.items.map((it, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-slate-600">{it.fuente}</span>
                  <span className={`tabular-nums font-semibold ${it.monto < 0 ? "text-red-600" : "text-emerald-700"}`}>
                    {it.monto < 0 ? "−" : ""}{fmtMoney(Math.abs(it.monto || 0))}
                  </span>
                </div>
              ))}
            </div>
            <div className={`text-right tabular-nums font-bold w-24 ${m.total < 0 ? "text-red-600" : "text-[#0F2D5C]"}`}>
              {m.total < 0 ? "−" : ""}{fmtMoney(Math.abs(m.total))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, mono, bold, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 ${mono ? "tabular-nums" : ""} ${bold ? "font-bold text-[#0F2D5C]" : "text-slate-800"}`}>
        {value || "—"}
      </div>
    </div>
  );
}
